import { useEffect, useRef } from "react";

import { distanceMeters } from "../services/tacticalMap";

const RING_SCALES_KM = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000];
const RINGS_MS = 900;
const SWEEP_MS = 2600;
const SWEEP_TRAIL = 22;
const PROBE_PX = 100;

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

const phase = (elapsed: number, start: number, duration: number) =>
  Math.max(0, Math.min((elapsed - start) / duration, 1));

export interface TacticalFocusProps {
  map: google.maps.Map | null;
  unitId: string | null;
  lat: number | null;
  lng: number | null;
  color: string;
  labelBackground: string;
}

export default function TacticalFocus({
  map,
  unitId,
  lat,
  lng,
  color,
  labelBackground,
}: TacticalFocusProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const paintRef = useRef<(() => void) | null>(null);
  const startedRef = useRef(0);

  const target = useRef({ lat, lng, color, labelBackground });
  target.current = { lat, lng, color, labelBackground };

  useEffect(() => {
    if (!map) return;

    const overlay = new google.maps.OverlayView();
    overlay.onAdd = () => {};
    overlay.onRemove = () => {};
    overlay.draw = () => paintRef.current?.();

    const paint = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) return;

      const rect = map.getDiv().getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.round(rect.width * dpr);
      const height = Math.round(rect.height * dpr);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);

      const projection = overlay.getProjection();
      const { lat: targetLat, lng: targetLng } = target.current;
      if (!projection || targetLat == null || targetLng == null) return;

      const center = projection.fromLatLngToContainerPixel(
        new google.maps.LatLng(targetLat, targetLng),
      );
      const left = projection.fromContainerPixelToLatLng(
        new google.maps.Point(0, rect.height / 2),
      );
      const right = projection.fromContainerPixelToLatLng(
        new google.maps.Point(PROBE_PX, rect.height / 2),
      );
      if (!center || !left || !right) return;

      const mpp =
        distanceMeters(
          { lat: left.lat(), lng: left.lng() },
          { lat: right.lat(), lng: right.lng() },
        ) / PROBE_PX;
      if (!Number.isFinite(mpp) || mpp <= 0) return;

      const elapsed = startedRef.current ? performance.now() - startedRef.current : SWEEP_MS;
      const maxRadius = Math.min(rect.width, rect.height) * 0.42;
      const maxKm = (maxRadius * mpp) / 1000;
      const scales = RING_SCALES_KM.filter((km) => km <= maxKm).slice(-3);
      if (scales.length === 0) scales.push(RING_SCALES_KM[0]);

      ctx.save();

      scales.forEach((km, index) => {
        const grow = easeOutCubic(phase(elapsed, index * 120, RINGS_MS));
        if (grow <= 0) return;
        const radius = ((km * 1000) / mpp) * grow;

        ctx.strokeStyle = target.current.color;
        ctx.globalAlpha = 0.3 * grow;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 5]);
        ctx.beginPath();
        ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);

        if (grow > 0.85) {
          const label = `${km} km`;
          ctx.font = "9px ui-monospace, SFMono-Regular, Menlo, monospace";
          const labelWidth = ctx.measureText(label).width;
          ctx.globalAlpha = 0.9;
          ctx.fillStyle = target.current.labelBackground;
          ctx.fillRect(center.x - labelWidth / 2 - 3, center.y - radius - 12, labelWidth + 6, 12);
          ctx.fillStyle = target.current.color;
          ctx.textAlign = "center";
          ctx.fillText(label, center.x, center.y - radius - 3);
        }
      });

      const life = 1 - phase(elapsed, SWEEP_MS - 500, 500);
      if (life > 0) {
        const angle = (elapsed / SWEEP_MS) * Math.PI * 2 - Math.PI / 2;
        const radius = (scales[scales.length - 1] * 1000) / mpp;
        for (let step = 0; step < SWEEP_TRAIL; step += 1) {
          const at = angle - step * 0.032;
          ctx.globalAlpha = 0.28 * life * (1 - step / SWEEP_TRAIL);
          ctx.strokeStyle = target.current.color;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(center.x, center.y);
          ctx.lineTo(center.x + Math.cos(at) * radius, center.y + Math.sin(at) * radius);
          ctx.stroke();
        }
      }

      ctx.restore();
    };

    paintRef.current = paint;
    overlay.setMap(map);

    return () => {
      overlay.setMap(null);
      paintRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    if (!map || !unitId) {
      startedRef.current = 0;
      paintRef.current?.();
      return;
    }

    startedRef.current = performance.now();

    let raf = requestAnimationFrame(function frame(now) {
      paintRef.current?.();
      if (now - startedRef.current < SWEEP_MS) raf = requestAnimationFrame(frame);
    });

    return () => cancelAnimationFrame(raf);
  }, [map, unitId]);

  useEffect(() => {
    paintRef.current?.();
  }, [lat, lng, color]);

  return <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />;
}
